import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Chip,
  CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, LinearProgress, MenuItem,
  Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import eventApi from '../EventGallery/eventApi';
import { swalConfirmDelete } from '../../utils/swal';
import ActiveAutoSyncPill from '../../components/common/ActiveAutoSyncPill';

const EVENT_TYPES = [
  'Wedding', 'Birthday', 'Reception', 'Baby Shower',
  'Corporate Meet', 'Conference', 'Cultural Celebration', 'Family Gathering',
  'Other',
];

const STATUS_COLOR = {
  none: 'default', processing: 'warning', uploading: 'warning',
  complete: 'success', failed: 'error',
};
const STATUS_LABEL = {
  none: 'Not grouped', processing: 'Processing', uploading: 'Uploading',
  complete: 'Ready', failed: 'Failed',
};

function CreateEventDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ event_name: '', event_type: '', event_date: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const handleChange = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.event_name.trim()) { setErr('Event name is required'); return; }
    setLoading(true); setErr('');
    try {
      const res = await eventApi.createEvent(form);
      onCreated(res);
      onClose();
      setForm({ event_name: '', event_type: '', event_date: '', description: '' });
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Typography color="error" variant="body2">{err}</Typography>}
          <TextField label="Event Name *" value={form.event_name} onChange={handleChange('event_name')} fullWidth />
          <TextField select label="Event Type" value={form.event_type} onChange={handleChange('event_type')} fullWidth>
            <MenuItem value="">- Select type -</MenuItem>
            {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Date" type="date" value={form.event_date} onChange={handleChange('event_date')} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


const EVENT_EMOJI = {
  Wedding: '💍', Birthday: '🎂', Reception: '🥂', 'Baby Shower': '👶',
  'Corporate Meet': '💼', Conference: '🎤', 'Cultural Celebration': '🎊', 'Family Gathering': '👨‍👩‍👧‍👦',
  Other: '📌',
};

function EventCard({ event, onClick, onDelete, deleting = false }) {
  const emoji = EVENT_EMOJI[event.event_type] || '📸';
  const gs = event.grouping_status || 'none';
  return (
    <Card
      variant="outlined"
      onClick={deleting ? undefined : onClick}
      sx={{
        borderRadius: 2,
        height: '100%',
        cursor: deleting ? 'default' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        opacity: deleting ? 0.7 : 1,
        transition: 'box-shadow 120ms ease, opacity 120ms ease',
        '&:hover': deleting ? {} : { boxShadow: 3 },
      }}
    >
        {deleting && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {emoji} {event.event_name}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                label={event.role === 'organiser' ? 'Organiser' : event.role === 'circle_viewer' ? 'Circle Viewer' : 'Attendee'}
                size="small"
                color={event.role === 'organiser' ? 'primary' : event.role === 'circle_viewer' ? 'secondary' : 'default'}
              />
              {event.role === 'organiser' && (
                <Button
                  size="small"
                  color="error"
                  disabled={deleting}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete?.(event);
                  }}
                  sx={{ minWidth: 32, width: 32, height: 28, p: 0 }}
                >
                  {deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                </Button>
              )}
            </Stack>
          </Stack>
          {deleting && (
            <Typography variant="caption" color="error" display="block" mt={0.75} fontWeight={700}>
              Deleting event...
            </Typography>
          )}
          {event.event_date && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {new Date(event.event_date).toLocaleDateString()}
            </Typography>
          )}
          {event.organiser_name && event.role !== 'organiser' && (
            <Typography variant="caption" color="text.secondary" display="block">
              By {event.organiser_name}
            </Typography>
          )}
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            {event.event_type && <Chip label={event.event_type} size="small" variant="outlined" />}
            <Chip
              label={STATUS_LABEL[gs] || gs}
              size="small"
              color={STATUS_COLOR[gs] || 'default'}
            />
          </Stack>
        </CardContent>
    </Card>
  );
}

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showNotification } = useNotification();
  const { isOrgContext, activeOrg, orgRole } = useAuth();
  const navigate = useNavigate();
  const canCreateEvent = !isOrgContext || orgRole === 'admin' || orgRole === 'organiser';

  const [createOpen, setCreateOpen] = useState(false);
  const [createdEvents, setCreatedEvents] = useState([]);
  const [joinedEvents, setJoinedEvents] = useState([]);
  const [circleSharedEvents, setCircleSharedEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [deletingEventIds, setDeletingEventIds] = useState(() => new Set());

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const res = await eventApi.listMyEvents();
      setCreatedEvents(res.created || []);
      setJoinedEvents(res.joined || []);
      setCircleSharedEvents(res.circle_shared || []);
    } catch {
      showNotification('Failed to load events', 'error');
    } finally {
      setLoadingEvents(false);
    }
  }, [showNotification]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const handleCreated = (res) => {
    showNotification(`Event created! Invite code: ${res.invite_code}`, 'success');
    loadEvents();
    navigate(`/event/${res.event_id}`);
  };

  const handleDeleteEvent = async (event) => {
    const ok = await swalConfirmDelete({
      title: `Delete ${event.event_name}?`,
      text: 'This deletes the event, requests, selections, and all event photos from storage. Account Memory will not be changed.',
      confirmText: 'Delete event',
    });
    if (!ok) return;
    setDeletingEventIds(prev => new Set(prev).add(event.event_id));
    try {
      await eventApi.deleteEvent(event.event_id);
      showNotification('Event deleted', 'success');
      loadEvents();
    } catch (err) {
      showNotification(err.response?.data?.error || err.message || 'Failed to delete event', 'error');
    } finally {
      setDeletingEventIds(prev => {
        const next = new Set(prev);
        next.delete(event.event_id);
        return next;
      });
    }
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: isMobile ? 8 : 0 }}>
      <ActiveAutoSyncPill />
      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* Org context banner */}
        {isOrgContext && (
          <Box
            sx={{
              mb: 3, p: 2, borderRadius: 2,
              bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}
          >
            <BusinessCenterIcon color="secondary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={700}>
                {activeOrg?.org_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Organisation context &middot; {orgRole} &mdash; showing events owned by this organisation
              </Typography>
            </Box>
            <Button size="small" variant="outlined" color="secondary"
              onClick={() => navigate(`/organisations/${activeOrg?.org_id}`)}>
              Manage
            </Button>
          </Box>
        )}

        {/* Welcome / story hero — only shown to genuinely first-run users */}
        {!loadingEvents && !isOrgContext && createdEvents.length === 0 && joinedEvents.length === 0 && circleSharedEvents.length === 0 && (
          <Box
            sx={{
              mb: 4, p: { xs: 2.5, sm: 3 }, borderRadius: 3,
              background: 'linear-gradient(135deg, #fff4f8 0%, #fff8ef 100%)',
              border: '1px solid', borderColor: 'divider',
            }}
          >
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Welcome to CircleMoments 👋
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
              Create an event, drop in everyone's photos, and we'll automatically sort them by face —
              so each guest only sees the moments they're actually in. No more chasing down WhatsApp threads.
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              {canCreateEvent && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  Create your first event
                </Button>
              )}
              <Button variant="outlined" startIcon={<QrCodeIcon />} onClick={() => navigate('/join-event')}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Join with an invite code
              </Button>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 2 }}>
              <AutoAwesomeIcon fontSize="small" color="secondary" />
              <Typography variant="body2" color="text.secondary">
                Faces you've named stick around too — visit{' '}
                <Box component="span" onClick={() => navigate('/memory')}
                  sx={{ color: 'secondary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                  Memory
                </Box>{' '}
                to see everyone CircleMoments remembers across your events.
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Quick Actions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Quick Actions</Typography>
          <Grid container spacing={2}>
            {canCreateEvent && (
              <Grid size={{ xs: 6 }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                    sx={{ py: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Create Event
                  </Button>
                </motion.div>
              </Grid>
            )}
            <Grid size={{ xs: canCreateEvent ? 6 : 12 }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button fullWidth variant="outlined" startIcon={<QrCodeIcon />} onClick={() => navigate('/join-event')}
                  sx={{ py: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  Join Event
                </Button>
              </motion.div>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button fullWidth variant="outlined" startIcon={<MailOutlineIcon />} onClick={() => navigate('/invitations')}
                  sx={{ py: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  Invitations
                </Button>
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        {/* Events */}
        <Box>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {isOrgContext ? `${activeOrg?.org_name} Events` : 'My Events'}
          </Typography>
          {loadingEvents ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <>
              {createdEvents.length > 0 && (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    {isOrgContext
                      ? <BusinessCenterIcon fontSize="small" color="secondary" />
                      : <EventIcon fontSize="small" color="primary" />}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {isOrgContext ? 'Organisation Events' : 'Events I Organised'}
                    </Typography>
                  </Stack>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {createdEvents.map(ev => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ev.event_id}>
                        <EventCard
                          event={ev}
                          onClick={() => navigate(`/event/${ev.event_id}`)}
                          onDelete={handleDeleteEvent}
                          deleting={deletingEventIds.has(ev.event_id)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {joinedEvents.length > 0 && (
                <>
                  {createdEvents.length > 0 && <Divider sx={{ mb: 2 }} />}
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <QrCodeIcon fontSize="small" color="action" />
                    <Typography variant="subtitle1" fontWeight={600}>Events I Joined</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {joinedEvents.map(ev => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ev.event_id}>
                        <EventCard event={ev} onClick={() => navigate(`/event/${ev.event_id}`)} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {circleSharedEvents.length > 0 && (
                <>
                  {(createdEvents.length > 0 || joinedEvents.length > 0) && <Divider sx={{ mb: 2 }} />}
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <GroupsIcon fontSize="small" color="action" />
                    <Typography variant="subtitle1" fontWeight={600}>Events shared via circles</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {circleSharedEvents.map(ev => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ev.event_id}>
                        <EventCard event={ev} onClick={() => navigate(`/event/${ev.event_id}`)} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {createdEvents.length === 0 && joinedEvents.length === 0 && circleSharedEvents.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography>
                    {isOrgContext
                      ? `No events under ${activeOrg?.org_name} yet. Create the first one!`
                      : 'Your events will show up here once you create or join one.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>

      <CreateEventDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
    </Box>
  );
};

export default Dashboard;
